import { BLACK, type Color, KING, TYPE_MASK, WHITE, WHITE_BIT, cheb, other, parseSquare, squareName } from '../board.ts';
import { KING_STEPS, SNAIL, pieceDef } from '../pieces.ts';
import { type MoveContext, draw, rulesFor, win } from '../rules.ts';
import { FenError } from '../notation.ts';
import type { Position, Variant } from '../types.ts';

// Day 1. Normal chess plus two snails. Each side owns a snail that hunts the *enemy* king:
// after every SNAIL_INTERVAL moves its owner makes, it takes one king-step towards that
// king. Snails are neutral pieces (no colour), so nothing can capture or pass through them,
// and they eat whatever is on the square they crawl onto, friend or foe.
// Leaving your king where the enemy snail lands next is illegal, like leaving it in check;
// with no legal move while that's the case it's "snailmate".

export const SNAIL_INTERVAL = 3;
export const SNAIL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1 a4,a5 0,0';

// ext layout: [white snail square, black snail square, white move count, black move count]
const COUNT = 2;

/** Moves `c` must still make before its snail steps (1 = the very next move). */
export function snailCountdown(pos: Position, c: Color): number {
  return SNAIL_INTERVAL - (pos.ext![COUNT + c] % SNAIL_INTERVAL);
}

/** True if `c`'s snail steps right after `c`'s next move. */
export function snailDue(pos: Position, c: Color): boolean {
  return pos.ext![c] >= 0 && (pos.ext![COUNT + c] + 1) % SNAIL_INTERVAL === 0;
}

/** Square of `c`'s snail (the one hunting the other king), -1 if none. */
export function snailSquare(pos: Position, c: Color): number {
  return pos.ext![c];
}

/** The side to move must get its king out of the enemy snail's next step. */
export function inSnailDanger(pos: Position): boolean {
  const us = pos.turn;
  const them = other(us);
  const k = pos.royals[us];
  return k >= 0 && snailDue(pos, them) && cheb(pos.ext![them], k) <= 1;
}

/**
 * Where `c`'s snail will step next, or -1 if it can't move. It takes the king-step that
 * gets it closest to the enemy king (ties: smaller straight-line distance, then the order
 * N, NE, E, SE, S, SW, W, NW). It never steps onto the other snail or its own king; if
 * every useful step is blocked it waits.
 */
export function snailTarget(ext: ArrayLike<number>, royals: ArrayLike<number>, c: Color): number {
  const s = ext[c];
  const k = royals[other(c)];
  if (s < 0 || k < 0) return -1;
  const d0 = cheb(s, k);
  if (d0 === 0) return -1;
  const sf = s & 7;
  const sr = s >> 3;
  const kf = k & 7;
  const kr = k >> 3;
  let best = -1;
  let bestD = 99;
  let bestE = 999;
  for (let i = 0; i < 8; i++) {
    const nf = sf + KING_STEPS[i][0];
    const nr = sr + KING_STEPS[i][1];
    if (nf < 0 || nf > 7 || nr < 0 || nr > 7) continue;
    const t = nr * 8 + nf;
    if (t === ext[other(c)] || t === royals[c]) continue;
    const d = Math.max(Math.abs(kf - nf), Math.abs(kr - nr));
    if (d >= d0) continue;
    const e = (kf - nf) * (kf - nf) + (kr - nr) * (kr - nr);
    if (d < bestD || (d === bestD && e < bestE)) {
      best = t;
      bestD = d;
      bestE = e;
    }
  }
  return best;
}

/** Penalty for a king by Chebyshev distance to the snail hunting it. */
const SNAIL_PRESSURE = [0, 60, 30, 10];
const hashScratch = new Int32Array(4);

export const snail: Variant = {
  id: 'snail',
  info: {
    name: 'SnailChess',
    but: 'snails hunt your king',
    tagline: 'Two unstoppable snails crawl toward the kings and eat everything in their way.',
    summary: 'Each side gets an **unstoppable snail**. Every 3rd move it crawls one square toward the enemy king and eats whatever it lands on.',
    rules: [
      'Each side owns a snail that hunts the enemy king. White’s starts on a4, Black’s on a5.',
      'After every 3rd move its owner makes, a snail crawls one square toward the enemy king.',
      'Snails can’t be captured or blocked, and they eat any piece they land on, even their own side’s.',
      'You may not leave your king where the enemy snail lands next. No way out is snailmate.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#5fcf7a',
    emoji: '🐌',
    bots: ['Hatchling', 'Garden Snail', 'Racing Snail', 'Turbo Snail', 'Grandmaster Snail'],
  },
  startFen: SNAIL_FEN,
  extraPieces: [SNAIL],
  extSize: 4,
  fenSkip: [SNAIL],

  parseExtra(fields, pos) {
    const ext = pos.ext!;
    const [snails = '-,-', counts = '0,0'] = fields;
    const squares = snails.split(',').map((s) => (s === '-' ? -1 : parseSquare(s)));
    if (squares.length !== 2 || squares.some((s, i) => s < -1 || (s === -1 && snails.split(',')[i] !== '-'))) {
      throw new FenError('Bad snail field');
    }
    for (const s of squares) if (s >= 0 && pos.board[s] !== 0) throw new FenError('Snail must be on an empty square');
    if (squares[0] >= 0 && squares[0] === squares[1]) throw new FenError('Snails cannot share a square');
    const n = counts.split(',').map(Number);
    if (n.length !== 2 || n.some((x) => !Number.isInteger(x) || x < 0)) throw new FenError('Bad move count field');
    for (const c of [WHITE, BLACK]) {
      ext[c] = squares[c];
      ext[COUNT + c] = n[c];
      if (squares[c] >= 0) pos.board[squares[c]] = SNAIL;
    }
  },

  formatExtra(pos) {
    const ext = pos.ext!;
    const sq = (s: number) => (s >= 0 ? squareName(s) : '-');
    return [`${sq(ext[0])},${sq(ext[1])}`, `${ext[2]},${ext[3]}`];
  },

  hashExt(pos) {
    const ext = pos.ext!;
    hashScratch[0] = ext[0];
    hashScratch[1] = ext[1];
    hashScratch[2] = ext[2] % SNAIL_INTERVAL;
    hashScratch[3] = ext[3] % SNAIL_INTERVAL;
    return hashScratch;
  },

  afterMove(ctx: MoveContext) {
    const { next, us } = ctx;
    const ext = next.ext!;
    ext[COUNT + us]++;
    if (ext[COUNT + us] % SNAIL_INTERVAL !== 0) return;
    const t = snailTarget(ext, next.royals, us);
    if (t < 0) return;
    ctx.relocate(ext[us], t, 'snail');
    ext[us] = t;
    if (next.ep >= 0 && (t === next.ep || t === next.ep + (us === WHITE ? 8 : -8))) next.ep = -1;
  },

  isLegal(ctx: MoveContext) {
    const { next, us, them } = ctx;
    const ext = next.ext!;
    const k = next.royals[us];
    return !(
      next.royals[them] >= 0 &&
      ext[them] >= 0 &&
      (ext[COUNT + them] + 1) % SNAIL_INTERVAL === 0 &&
      cheb(ext[them], k) <= 1
    );
  },

  threatened: inSnailDanger,

  terminal(pos) {
    if (pos.royals[WHITE] < 0) return win(BLACK, 'king-eaten');
    if (pos.royals[BLACK] < 0) return win(WHITE, 'king-eaten');
    return null;
  },

  noMoves(pos) {
    const rules = rulesFor(snail);
    if (rules.inCheck(pos)) return win(other(pos.turn), 'checkmate');
    if (inSnailDanger(pos)) return win(other(pos.turn), 'snailmate');
    return draw('stalemate');
  },

  evaluate(pos) {
    const ext = pos.ext!;
    let score = 0;
    for (const c of [WHITE, BLACK] as Color[]) {
      const s = ext[c];
      const king = pos.royals[other(c)];
      if (s < 0 || king < 0) continue;
      const forHunter = c === WHITE ? 1 : -1;
      const d = cheb(s, king);
      if (d < SNAIL_PRESSURE.length) {
        const kf = king & 7;
        const kr = king >> 3;
        const edges = (kf === 0 || kf === 7 ? 1 : 0) + (kr === 0 || kr === 7 ? 1 : 0);
        score += forHunter * (SNAIL_PRESSURE[d] + edges * 15);
      }
      const target = snailTarget(ext, pos.royals, c);
      if (target < 0 || target === king) continue;
      const victim = pos.board[target];
      if (victim === 0 || victim === SNAIL) continue;
      const owner: Color = victim & WHITE_BIT ? WHITE : BLACK;
      const movesLeft = SNAIL_INTERVAL - (ext[COUNT + c] % SNAIL_INTERVAL);
      let frac = 0.05;
      if (movesLeft === 1) frac = pos.turn === c && owner !== c ? 0.85 : 0.3;
      else if (movesLeft === 2) frac = 0.12;
      const type = victim & TYPE_MASK;
      score += (owner === WHITE ? -1 : 1) * (type === KING ? 0 : pieceDef(type).value) * frac;
    }
    return score;
  },

  reasonText: {
    snailmate: 'Snailmate: the snail got the king',
    'king-eaten': 'The snail ate the king',
  },
};
